import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

interface PaginationProps {
  page: number;
  rowsPerPage: number;
  totalItems: number;
  totalPages: number;
  startItem: number;
  endItem: number;
  onRowsPerPageChange: (event: any) => void;
  onGoToNextPage: () => void;
  onGoToPreviousPage: () => void;
  rowsPerPageOptions?: number[];
  showRowsPerPage?: boolean;
  className?: string;
  sx?: any;
}

const Pagination: React.FC<PaginationProps> = ({
  page,
  rowsPerPage,
  totalItems,
  totalPages,
  startItem,
  endItem,
  onRowsPerPageChange,
  onGoToNextPage,
  onGoToPreviousPage,
  rowsPerPageOptions = [5, 10, 25, 50],
  showRowsPerPage = true,
  className,
  sx,
}) => {
  return (
    <Box
      className={`d-flex flex-wrap justify-content-end align-items-center gap-3 ${className || ''}`}
      sx={{ pt: 2, ...sx }}
    >
      {/* Items per page */}
      {showRowsPerPage && (
        <Box className="d-flex align-items-center gap-2">
          <Typography
            variant="body2"
            sx={{ fontSize: '14px', fontWeight: 500 }}
          >
            Item Per Page:
          </Typography>
          <FormControl size="small">
            <Select
              value={rowsPerPage}
              onChange={onRowsPerPageChange}
              sx={{
                fontSize: '14px',
                height: '32px',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ced4da' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#adb5bd' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0d6efd' },
                '& .MuiSelect-select': { padding: '6px 10px' },
              }}
            >
              {rowsPerPageOptions.map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  sx={{ fontSize: '14px', fontWeight: 500 }}
                >
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Page info */}
      <Typography
        variant="body2"
        sx={{ fontSize: '14px', fontWeight: 500 }}
      >
        {startItem} - {endItem} of {totalItems}
      </Typography>

      {/* Navigation arrows */}
      <Box className="d-flex align-items-center gap-2">
        <IconButton
          onClick={onGoToPreviousPage}
          disabled={page === 0}
          sx={{
            width: 32,
            height: 32,
            color: page === 0 ? '#ccc' : '#6c757d',
            '&:hover': {
              backgroundColor: page === 0 ? 'transparent' : '#e3f2fd',
              color: page === 0 ? '#ccc' : '#1976d2',
            },
          }}
        >
          <ChevronLeft fontSize="small" />
        </IconButton>

        <IconButton
          onClick={onGoToNextPage}
          disabled={page >= totalPages - 1}
          sx={{
            width: 32,
            height: 32,
            color: page >= totalPages - 1 ? '#ccc' : '#6c757d',
            '&:hover': {
              backgroundColor: page >= totalPages - 1 ? 'transparent' : '#e3f2fd',
              color: page >= totalPages - 1 ? '#ccc' : '#1976d2',
            },
          }}
        >
          <ChevronRight fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default Pagination;
